import {
  createAsyncThunk,
  createSlice,
  isAnyOf,
  PayloadAction,
} from '@reduxjs/toolkit'
import okaoAxios, { okaoAxiosNoAuth } from '../helpers/axios'
import {
  DiningReview,
  IRestaurantDishReviewRequest,
  IRestaurantName,
  IRestaurantReview,
  IRestaurantSentPayloadReview,
  PredictedRestaurantRequest,
  Recommendation,
  Restaurant,
  RestaurantAndPredictedRestaurant,
  RestaurantResponse,
  RestaurantResponseContainer,
} from '../types'
import { RootState } from './'
import { AlertColor } from '@mui/material/Alert'
import { PredictedDish } from '../types/menuTypes'
import { convertToPredictedAndPublicRestaurant } from '../helpers/converters'
import { infoLog } from '../utils/log-helper'

export interface IRestaurantSlice {
  restaurants: RestaurantResponse[] | undefined
  onboardRestaurants: IRestaurantName[] | undefined
  status: string
  shouldOpen: boolean
  message: string | undefined
  severity: AlertColor | undefined
  restaurant: RestaurantAndPredictedRestaurant | undefined
  restaurantDishList: PredictedDish[] | undefined
  radius: number
  menuNameList: string[]
  publicRestaurant: Restaurant | undefined
  predictedRestaurants: RestaurantResponseContainer
  matchDescription?: string
  isMatchDetailsLoading: boolean
  matchDetailsError?: string | null
}

const initialState: IRestaurantSlice = {
  restaurants: undefined,
  onboardRestaurants: undefined,
  status: 'idle',
  shouldOpen: false,
  message: undefined,
  severity: undefined,
  restaurant: undefined,
  restaurantDishList: undefined,
  radius: 5,
  menuNameList: [],
  predictedRestaurants: {},
  publicRestaurant: undefined,
  matchDescription: undefined,
  isMatchDetailsLoading: false,
  matchDetailsError: null,
}

export const fetchRestaurants = createAsyncThunk(
  'api/fetchRestaurants',
  async (page: number) => {
    const response = await okaoAxios.get(`/restaurants/${page}`)
    return response?.data
  },
)

export const fetchOnboardRestaurants = createAsyncThunk(
  'api/fetchOnboardRestaurants',
  async () => {
    const response = await okaoAxios.get(`/restaurants/onboarded`)
    return response?.data
  },
)

export const fetchPublicRestaurantByAlias = createAsyncThunk(
  'api/fetchRestaurantByAlias',
  async (alias: string) => {
    const response = await okaoAxiosNoAuth().get(
      `/restaurants/public/${alias}/details`,
    )
    return response?.data
  },
)

export const getPredictedRestaurants = createAsyncThunk(
  'api/getPredictedRestaurants',
  async (payload: PredictedRestaurantRequest) => {
    const response = await okaoAxios.post(`/restaurants/predict`, payload)
    return response?.data
  },
)

// Note: SaveRestaurantReviewResponse removed - now using EaterRestaurantReviewsResponse consistently

export const saveRestaurantReviewsByAlias = createAsyncThunk<
  EaterRestaurantReviewsResponse,
  IRestaurantSentPayloadReview
>(
  'api/saveDiningReviewsByAlias',
  async (payload: IRestaurantSentPayloadReview) => {
    const formData = new FormData()
    payload?.images?.forEach((img) => {
      formData.append('files', img)
    })

    if (payload.starCount) formData.append('starCount', `${payload.starCount}`)
    if (payload.value) formData.append('value', payload.value)

    const appendAspect = (key: string, val?: boolean): void => {
      if (val !== undefined && val !== null) formData.append(key, `${val}`)
    }
    appendAspect('likesCuisine', payload.likesCuisine)
    appendAspect('likesAmbience', payload.likesAmbience)
    appendAspect('likesStaff', payload.likesStaff)
    appendAspect('likesWaitingTime', payload.likesWaitingTime)
    appendAspect('likesPrice', payload.likesPrice)
    appendAspect('likesMenu', payload.likesMenu)
    appendAspect('wouldReturn', payload.wouldReturn)

    const response = await okaoAxios.post(
      `/reviews/${payload.alias as string}/dining-review`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )

    // Proxy returns EaterRestaurantReviewsResponse consistently
    return response?.data as EaterRestaurantReviewsResponse
  },
)

/** Anonymous (logged-out) review submission — JSON to the public endpoint, tagged with anonId. */
export const saveAnonRestaurantReview = createAsyncThunk(
  'api/saveAnonRestaurantReview',
  async (payload: IRestaurantSentPayloadReview) => {
    const response = await okaoAxios.post(
      `/reviews/public/${payload.alias as string}/dining-review`,
      {
        anonId: payload.anonId,
        starCount: payload.starCount,
        value: payload.value,
        likesCuisine: payload.likesCuisine,
        likesAmbience: payload.likesAmbience,
        likesStaff: payload.likesStaff,
        likesWaitingTime: payload.likesWaitingTime,
        likesPrice: payload.likesPrice,
        likesMenu: payload.likesMenu,
        wouldReturn: payload.wouldReturn,
      },
    )
    return response?.data
  },
)

/** Claim-on-signup: links this device's anonymous reviews to the now-authenticated account. */
export const claimAnonReviews = createAsyncThunk(
  'api/claimAnonReviews',
  async (anonId: string) => {
    const response = await okaoAxios.post(`/reviews/claim`, { anonId })
    return response?.data
  },
)
export const updateRestaurantReviewsByAlias = createAsyncThunk<
  EaterRestaurantReviewsResponse,
  IRestaurantSentPayloadReview
>(
  'api/updateRestaurantReviewsByAlias',
  async (payload: IRestaurantSentPayloadReview) => {
    const formData = new FormData()
    payload?.images?.forEach((img) => {
      formData.append('files', img)
    })

    // Safety guard: filter out URLs (should only receive reviewImageId values)
    const deletedIds = (payload?.deleted ?? []).filter((id) => {
      if (typeof id === 'string' && id.startsWith('http')) {
        infoLog(
          '[updateRestaurantReviewsByAlias] Filtered out URL from deleted array. Expected reviewImageId, got:',
          id,
        )
        return false
      }
      return true
    })

    deletedIds.forEach((id) => {
      formData.append('deleted', id)
    })
    formData.append('starCount', `${payload.starCount ?? 0}`)
    formData.append('value', payload.value ?? '')

    const response = await okaoAxios.put(
      `/reviews/${payload.alias as string}/dining-review`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    )
    
    // Proxy returns EaterRestaurantReviewsResponse consistently
    return response?.data as EaterRestaurantReviewsResponse
  },
)

// Response type for dish review save
export interface DiningReviewResponse {
  diningReviewId: string
  dishId: string
  restaurantAlias: string
  starCount: number
  value?: string
  sentimentSummary?: string
  sentimentLabel?: string
  sentimentScore?: number
  sentimentTags?: string
  created?: string
  updated?: string
  // Additional fields that may be present
  name?: string
  email?: string
  location?: string
  menuId?: string
}

export const saveRestaurantDish = createAsyncThunk<
  DiningReviewResponse,
  IRestaurantDishReviewRequest
>(
  'api/saveRestaurantDish',
  async (payload: IRestaurantDishReviewRequest) => {
    const data = {
      starCount: payload.starCount,
    }
    const response = await okaoAxios.post(
      `/reviews/${payload.alias ?? ''}/dining-review/${payload.dishId ?? ''}`,
      data,
    )
    return response?.data as DiningReviewResponse
  },
)

export const favoriteRestaurant = createAsyncThunk(
  'api/favoriteRestaurant',
  async (arg: string | { alias: string; name?: string }) => {
    const alias = typeof arg === 'string' ? arg : arg.alias
    const fallbackName = typeof arg === 'string' ? undefined : arg.name
    const response = await okaoAxios.post(`/eater/favorite/${alias}`)
    const data = (response?.data ?? {}) as Record<string, unknown>
    // Prefer the name from the response; fall back to the caller-supplied name so the success
    // toast never renders "undefined" (the favorite response can omit the name).
    return {
      ...data,
      name: (data.name as string) ?? fallbackName,
      restaurantId: data.restaurantId as string | undefined,
    }
  },
)

export const deleteRestaurantReview = createAsyncThunk(
  'api/deleteRestaurantReview',
  async (id: string) => {
    const response = await okaoAxios.delete(`/reviews/${id}`)
    return response?.data
  },
)

export interface EaterRestaurantReviewsResponse {
  restaurantReview: IRestaurantReview | null
  dishReviews?: DiningReview[] // Optional - may be missing in some responses
  // Note: Proxy now consistently returns this shape for both GET and POST/PUT
}

export const fetchRestaurantReviewsByAlias = createAsyncThunk<
  EaterRestaurantReviewsResponse,
  { alias: string }
>('restaurant/fetchRestaurantReviewsByAlias', async ({ alias }) => {
  const response = await okaoAxios.get(`/reviews/${alias}/dining-review`)
  const data = response.data as EaterRestaurantReviewsResponse
  // Ensure dishReviews is always an array (never undefined)
  return {
    ...data,
    dishReviews: data.dishReviews ?? [],
  }
})

export interface RestaurantMatchDetailsResponse {
  restaurantAlias: string
  matchDescription: string
  matchQualityText?: string
  matchPercentage?: number
}

export const fetchRestaurantMatchDetails = createAsyncThunk<
  RestaurantMatchDetailsResponse,
  { alias: string; anonId?: string },
  { state: RootState }
>('restaurant/fetchMatchDetails', async ({ alias, anonId }, thunkApi) => {
  // Anon/logged-out callers hit the permitAll public variant (resolves a guest eater by anonId);
  // authenticated callers use the JWT-scoped route. Both reuse the same backend match pipeline.
  const url = anonId
    ? `/restaurants/public/${alias}/match-details?anonId=${encodeURIComponent(anonId)}`
    : `/restaurants/${alias}/match-details`
  const response = await okaoAxios.get(url)
  return response?.data
})

export const restaurantSlice = createSlice({
  name: 'restaurant',
  initialState,
  reducers: {
    setRestaurants: (
      state,
      action: PayloadAction<RestaurantResponse[] | undefined>,
    ) => {
      state.restaurants = action.payload
    },
    setOpen: (state, action: PayloadAction<boolean>) => {
      state.shouldOpen = action.payload
    },
    setRadius: (state, action: PayloadAction<number>) => {
      state.radius = action.payload
    },
    setMessage: (state, action: PayloadAction<string>) => {
      state.message = action.payload
    },
    setSeverity: (state, action: PayloadAction<AlertColor>) => {
      state.severity = action.payload
    },
    setPredictedRestaurantDetail: (
      state,
      action: PayloadAction<{ alias: string }>,
    ) => {
      const predicted = state.predictedRestaurants.result?.find(
        (restaurant) => restaurant.restaurantDTO.alias === action.payload.alias,
      )

      if (predicted) {
        state.restaurant = predicted

        state.restaurantDishList = predicted?.restaurantDTO?.menus
          ?.map((menu) => {
            if (!state.menuNameList.includes(menu.name)) {
              state.menuNameList.push(menu.name)
            }
            
            // Prefer predictedDishes when present and non-empty
            if (menu.predictedDishes && menu.predictedDishes.length > 0) {
              return menu.predictedDishes.map((dish) => {
                return {
                  ...dish,
                  menuName: menu.name,
                  menuId: menu.menuID,
                }
              })
            }
            
            // Fallback to menu.dishes when predictedDishes is empty
            if (menu.dishes && menu.dishes.length > 0) {
              return menu.dishes.map((dish) => {
                // Synthesize PredictedDish shape from raw dish
                let probability: Recommendation = {
                  yes: 0,
                  no: 1,
                  matchLabel: 'Low match',
                };

                // Look up probability from restaurant-level recommendations
                const matchingRec = predicted.restaurantDTO.recommendations?.find(
                  (rec) => rec.dishId === dish.dishID
                );
                if (matchingRec?.recommendation) {
                  probability = matchingRec.recommendation;
                }

                return {
                  dish,
                  menuName: menu.name,
                  menuId: menu.menuID,
                  probability,
                }
              })
            }

            return []
          })
          .flat()
      } else {
        state.restaurant = undefined
      }
    },
    setRestaurantDetailFromResponse: (
      state,
      action: PayloadAction<RestaurantAndPredictedRestaurant>,
    ) => {
      state.restaurant = action.payload
      state.restaurantDishList = action.payload?.restaurantDTO?.menus
        ?.map((menu) => {
          if (!state.menuNameList.includes(menu.name)) {
            state.menuNameList.push(menu.name)
          }
          
          // Prefer predictedDishes when present and non-empty
          if (menu.predictedDishes && menu.predictedDishes.length > 0) {
            return menu.predictedDishes.map((dish) => {
              return {
                ...dish,
                menuName: menu.name,
                menuId: menu.menuID,
              }
            })
          }
          
          // Fallback to menu.dishes when predictedDishes is empty
          if (menu.dishes && menu.dishes.length > 0) {
            return menu.dishes.map((dish) => {
              // Synthesize PredictedDish shape from raw dish
              let probability: Recommendation = {
                yes: 0,
                no: 1,
                matchLabel: 'Low match',
              };

              // Look up probability from restaurant-level recommendations
              const matchingRec = action.payload.restaurantDTO.recommendations?.find(
                (rec) => rec.dishId === dish.dishID
              );
              if (matchingRec?.recommendation) {
                probability = matchingRec.recommendation;
              }

              return {
                dish,
                menuName: menu.name,
                menuId: menu.menuID,
                probability,
              }
            })
          }

          return []
        })
        .flat()
    },
    setPublicRestaurantDetail: (state) => {
      if (state.publicRestaurant) {
        state.restaurant = convertToPredictedAndPublicRestaurant(
          state.publicRestaurant,
        )
        state.restaurantDishList = state.publicRestaurant?.menus
          ?.map((menu) => {
            if (!state.menuNameList.includes(menu.name)) {
              state.menuNameList.push(menu.name)
            }
            return menu.dishes.map((dish) => {
              return {
                probability: {
                  no: 0,
                  yes: 0,
                },
                dish,
                menuName: menu.name,
                menuId: menu.menuID,
              }
            })
          })
          .flat()
      }
    },
    clearData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPublicRestaurantByAlias.fulfilled, (state, action) => {
        state.menuNameList = []
        // Normalize imageUrl - handle both imageUrl and imageurl from API
        const payload = action.payload as Restaurant & { imageurl?: string }
        const normalizedRestaurant: Restaurant = {
          ...payload,
          imageUrl: payload.imageUrl ?? payload.imageurl,
        }
        // Remove legacy imageurl property if it exists
        if ('imageurl' in normalizedRestaurant) {
          delete (normalizedRestaurant as any).imageurl
        }
        state.publicRestaurant = normalizedRestaurant
        state.status = 'idle'
      })
      .addCase(getPredictedRestaurants.fulfilled, (state, action) => {
        state.menuNameList = []
        // Normalize imageUrl in predicted restaurants - handle both imageUrl and imageurl from API
        const normalizedPayload = {
          ...action.payload,
          result: action.payload.result?.map((restaurant) => {
            const dtoPayload = restaurant.restaurantDTO as Restaurant & {
              imageurl?: string
            }
            const normalizedDTO: Restaurant = {
              ...dtoPayload,
              imageUrl: dtoPayload.imageUrl ?? dtoPayload.imageurl,
            }
            // Remove legacy imageurl property if it exists
            if ('imageurl' in normalizedDTO) {
              delete (normalizedDTO as any).imageurl
            }
            return { ...restaurant, restaurantDTO: normalizedDTO }
          }),
        }
        state.predictedRestaurants = normalizedPayload
        state.status = 'idle'
      })
      .addCase(fetchRestaurants.fulfilled, (state, action) => {
        state.restaurants = action.payload.result
        state.status = 'idle'
      })
      .addCase(fetchOnboardRestaurants.fulfilled, (state, action) => {
        state.onboardRestaurants = action.payload.restaurants
        state.status = 'idle'
      })
      .addCase(favoriteRestaurant.fulfilled, (state, action) => {
        state.status = 'idle'
        state.shouldOpen = true
        state.severity = 'success'
        const savedName = action.payload?.name || 'Restaurant'
        state.message = `${savedName} has been saved successfully.`
      })
      .addCase(saveRestaurantDish.fulfilled, (state, action) => {
        state.status = 'idle'
        state.shouldOpen = true
        state.severity = 'success'
        state.message = 'dish review saved successfully.'
        // Note: Dish review update is handled in DishesTab component
        // to immediately update eaterDishReviews and trigger refetch
      })
      .addCase(
        (saveRestaurantReviewsByAlias.rejected,
        updateRestaurantReviewsByAlias.rejected),
        (state, action) => {
          state.shouldOpen = true
          state.message =
            'An error occurred while saving the restaurant review. Please try again later.'
          state.status = 'error'
          state.severity = 'error'
        },
      )
      .addCase(deleteRestaurantReview.rejected, (state, action) => {
        state.shouldOpen = true
        state.message =
          'An error occurred while delete the restaurant review. Please try again later.'
        state.status = 'error'
        state.severity = 'error'
      })
      .addCase(fetchRestaurantReviewsByAlias.fulfilled, (state) => {
        state.status = 'idle'
        // Profile state updates will be handled in ReviewsTab
      })
      .addCase(fetchRestaurantReviewsByAlias.rejected, (state, action) => {
        state.status = 'idle'
        // Optionally show error, but don't block the UI
      })
      .addCase(fetchRestaurantMatchDetails.pending, (state) => {
        state.isMatchDetailsLoading = true
        state.matchDetailsError = null
        state.matchDescription = undefined // Clear stale summary
      })
      .addCase(fetchRestaurantMatchDetails.fulfilled, (state, action) => {
        state.isMatchDetailsLoading = false
        state.matchDescription = action.payload.matchDescription
        state.matchDetailsError = null
      })
      .addCase(fetchRestaurantMatchDetails.rejected, (state, action) => {
        state.isMatchDetailsLoading = false
        state.matchDescription = undefined // Clear stale summary on error
        state.matchDetailsError =
          action.error.message ?? 'Unable to load match details'
      })
      .addMatcher(
        isAnyOf(
          fetchOnboardRestaurants.rejected,
          fetchRestaurants.rejected,
          fetchPublicRestaurantByAlias.rejected,
          getPredictedRestaurants.rejected,
          favoriteRestaurant.rejected,
          saveRestaurantDish.rejected,
        ),
        (state, action) => {
          state.shouldOpen = true
          state.severity = 'error'
          state.status = 'error'
          state.message =
            'Oops! Looks like something went wrong on our end. But don’t worry, a quick refresh might just do the trick! Give it a whirl and let’s try this again. If the problem persists, we’ll be here to sort it out!'
        },
      )
      .addMatcher(
        isAnyOf(
          fetchOnboardRestaurants.pending,
          fetchRestaurants.pending,
          fetchPublicRestaurantByAlias.pending,
          getPredictedRestaurants.pending,
          saveRestaurantReviewsByAlias.pending,
          updateRestaurantReviewsByAlias.pending,
          favoriteRestaurant.pending,
          deleteRestaurantReview.pending,
        ),
        (state, action) => {
          state.status = 'loading'
        },
      )
      // Match details loading doesn't affect main status - keep it silent
  },
})

export const selectOnboardRestaurants = (state: RootState): IRestaurantName[] =>
  state.restaurant.onboardRestaurants ?? []

export const selectIsLoading = (state: RootState): boolean =>
  state.restaurant.status === 'loading'

export const selectShouldOpen = (state: RootState): boolean =>
  state.restaurant.shouldOpen
export const selectMessage = (state: RootState): string | undefined =>
  state.restaurant.message
export const selectSeverity = (state: RootState): AlertColor | undefined =>
  state.restaurant.severity

export const selectRadius = (state: RootState): number => {
  return state?.restaurant?.radius
}
export const selectRestaurantDetail = (
  state: RootState,
): RestaurantAndPredictedRestaurant | undefined => state.restaurant.restaurant
export const selectRestaurantDishList = (
  state: RootState,
): PredictedDish[] | undefined => state.restaurant.restaurantDishList
export const selectMenuNameList = (state: RootState): string[] =>
  state.restaurant.menuNameList

export const selectRestaurantMatchDescription = (
  state: RootState,
): string | undefined => state.restaurant.matchDescription

export const selectIsMatchDetailsLoading = (state: RootState): boolean =>
  state.restaurant.isMatchDetailsLoading

export const selectMatchDetailsError = (
  state: RootState,
): string | null | undefined => state.restaurant.matchDetailsError

export const {
  setRadius,
  setOpen,
  setPublicRestaurantDetail,
  setPredictedRestaurantDetail,
  setRestaurantDetailFromResponse,
  setMessage,
  setSeverity,
  clearData,
} = restaurantSlice.actions

export default restaurantSlice.reducer
